import axios from "axios";
import { formatSize } from "@/lib/sizes";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "fittin_fitkey";

// The FitKey (a Wallet pass) IS the identity. No accounts, no passwords.
// We keep the last created key on-device so the hero / pass page can show it.
export function saveLocalFitKey(fitkey) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(fitkey));
  } catch (_e) {
    /* storage unavailable — fine */
  }
}

export function loadLocalFitKey(fitkeyId = null) {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return null;
    const fk = JSON.parse(raw);
    if (fitkeyId && fk?.fitkey_id !== fitkeyId) return null;
    return fk;
  } catch (_e) {
    return null;
  }
}

function previewId() {
  const rnd = Math.random().toString(36).slice(2, 8);
  return `fk_preview_${Date.now().toString(36)}${rnd}`;
}

export const isPreviewId = (id) => typeof id === "string" && id.startsWith("fk_preview_");

// POST /api/fitkey. The response carries the secret `access_key` ONCE: it is
// handed back separately (to start a session right away) and is never stored
// with the FitKey record or logged. Any non-2xx / network error resolves to a
// soft "preview" FitKey so the card still renders, never an error wall.
export async function createFitKey(payload) {
  try {
    const res = await axios.post(`${API}/fitkey`, payload, { timeout: 15000 });
    const { access_key: accessKey, ...d } = res?.data || {};
    if (!d.fitkey_id) throw new Error("no fitkey_id");
    return {
      accessKey: accessKey || null,
      fitkey: {
        live: true,
        fitkey_id: d.fitkey_id,
        recommended_eu_size: d.recommended_eu_size ?? null,
        size_display: d.size_display || null,
        foot_length_cm: d.foot_length_cm ?? null,
        foot_width_cm: d.foot_width_cm ?? null,
        fit_allowance_cm: d.fit_allowance_cm ?? null,
        fit_preference: d.fit_preference || null,
        reference_check: d.reference_check || null,
        brand_name: d.reference_brand || null,
        apple_wallet_url: d.apple_wallet_url || null,
        google_wallet_url: d.google_wallet_url || null,
      },
    };
  } catch (_e) {
    return {
      accessKey: null,
      fitkey: {
        live: false,
        fitkey_id: previewId(),
        recommended_eu_size: null,
        size_display: null,
        apple_wallet_url: null,
        google_wallet_url: null,
      },
    };
  }
}

// Public view of a FitKey (GET /api/fitkey/{id}); null when unknown/unreachable.
export async function fetchPublicFitKey(fitkeyId) {
  try {
    const r = await axios.get(`${API}/fitkey/${encodeURIComponent(fitkeyId)}`, { timeout: 12000 });
    return { ok: true, data: r.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status || 0 };
  }
}

// Generic soft POST — returns { ok, data } and never throws.
export async function softPost(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, { timeout: 12000 });
    return { ok: true, data: res.data };
  } catch (e) {
    return { ok: false, status: e?.response?.status || 0 };
  }
}

// Headline size for the pass. Only ever shows numbers we actually have:
// the backend's EU recommendation, or the reference size in the user's own unit.
// Always with fractions (42½, 45⅓): the backend's `size_display` first.
export function passSize({ recommended_eu_size, size_display, reference_size, reference_unit, reference_size_system }) {
  if (size_display) return { unit: "EU", value: size_display };
  if (recommended_eu_size != null && recommended_eu_size !== "") {
    return { unit: "EU", value: formatSize(recommended_eu_size) };
  }
  if (reference_size) {
    return { unit: reference_unit || reference_size_system || "EU", value: formatSize(reference_size) };
  }
  return { unit: "EU", value: "—" };
}
